import { useEffect, useRef, useState } from "react";
function AnimationComponent({
  animationUrl,
  loadingText = undefined,
  rotateValue,
  width,
  height,
  heightContainer = "auto",
  isDesktop,
  parentStep,
  stateMachines = [],
  animations = [],
  artboard,
  autoplay = true,
  pointerEvents,
  onLoad,
  onError,
  onPlay,
  onPause,
  onStop,
}) {
  const canvasRef = useRef(null);
  const riveInstanceRef = useRef(null);
  const [riveLoaded, setRiveLoaded] = useState(true);
  const [, setHasError] = useState(false);
  const callbacksRef = useRef({ onLoad, onError, onPlay, onPause, onStop });
  callbacksRef.current = { onLoad, onError, onPlay, onPause, onStop };

  useEffect(() => {
    let mounted = true;

    const initRive = async () => {
      try {
        if (!canvasRef.current) return;

        // Importar Rive dinámicamente
        const { Rive, Layout, Fit, Alignment } = await import(
          "@rive-app/canvas"
        );

        if (!mounted) return;

        const riveInstance = new Rive({
          src: animationUrl,
          canvas: canvasRef.current,
          autoplay: autoplay,
          animations: animations,
          layout: new Layout({
            fit: Fit.Contain,
            alignment: Alignment.Center,
          }),
          stateMachines: stateMachines,
          artboard: artboard,
          useOffscreenRenderer: true,
          enableRiveAssetCDN: true,
          onLoad: () => {
            if (mounted) {
              setRiveLoaded(true);
              setHasError(false);
              callbacksRef.current.onLoad?.();
            }
          },
          onLoadError: (error) => {
            if (mounted) {
              console.error("❌ Error cargando Rive:", error);
              setHasError(true);
              callbacksRef.current.onError?.();
            }
          },

          onPlay: () => {
            if (mounted) {
              /* console.log("▶️ Animación Rive iniciada"); */
              callbacksRef.current.onPlay?.();
            }
          },
          onPause: () => {
            if (mounted) {
              /* console.log("⏸️ Animación Rive pausada"); */
              callbacksRef.current.onPause?.();
            }
          },
          onStop: () => {
            if (mounted) {
              /* console.log("⏹️ Animación Rive detenida"); */
              callbacksRef.current.onStop?.();
            }
          },
        });

        riveInstanceRef.current = riveInstance;
      } catch (error) {
        if (mounted) {
          console.error("❌ Error inicializando Rive:", error);
          setHasError(true);
        }
      }
    };

    initRive();

    return () => {
      mounted = false;
      if (riveInstanceRef.current) {
        try {
          riveInstanceRef.current.cleanup();
        } catch (error) {
          console.warn("Error limpiando Rive:", error);
        }
        riveInstanceRef.current = null;
      }
    };
    // onError/onLoad/onPause/onPlay/onStop se leen vía callbacksRef.current (no como
    // dependencias): son callbacks del padre no memoizados, así que incluirlos recrearía la
    // instancia de Rive en cada render del padre.
  }, [animationUrl, autoplay, stateMachines, animations, artboard]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    const dpr = window.devicePixelRatio || 1;

    // Tamaño visual del canvas (el que recibes como width/height)
    const displayWidth = width;
    const displayHeight = height;

    // Aumentar el tamaño REAL del bitmap
    canvas.width = displayWidth * dpr;
    canvas.height = displayHeight * dpr;

    // Tamaño visual (lo que tú ves)
    canvas.style.width = displayWidth + "px";
    canvas.style.height = displayHeight + "px";

    // Escalar el contexto para que no se vea gigante
    ctx.scale(dpr, dpr);

    // Aquí cargas y dibujas tu animación de Rive normalmente...
  }, [width, height]);

  return (
    <div
      className="flex flex-col items-center justify-center"
      style={{
        height: heightContainer,
      }}
    >
      {/* Canvas de Rive */}
      <canvas
        ref={canvasRef}
        className={`transition-opacity duration-500 
      "opacity-100"
        `}
        style={{
          rotate: rotateValue,
          width: width,
          height: height,
          pointerEvents: pointerEvents,
        }}
      />

      {/* Texto de cargando */}
      <div className="mt-4">
        <p
          style={{
            textAlign: "center",
            width: "250px",
          }}
        >
          {loadingText === "" ||
          loadingText === null ||
          loadingText === undefined
            ? ""
            : loadingText}
        </p>
        <p
          style={{
            textAlign: "center",
          }}
        >
          {loadingText === "" ||
          loadingText === null ||
          loadingText === undefined
            ? ""
            : "..."}
        </p>
      </div>

      {/* Loading spinner mientras carga Rive */}
      {!riveLoaded && (
        <div className="absolute">
          <div
            className="w-14 h-14 rounded-full border-4 border-gray-300 border-t-purple-500"
            style={{ animation: "spin 1s linear infinite" }}
          />
          <style>
            {`
              @keyframes spin {
                to { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
    </div>
  );
}

export default AnimationComponent;
